import React, { useEffect, useMemo, useState } from 'react';
import { searchQuoteCatalogProducts } from '../quoteCatalog.api';
import {
  isCatalogPriceExpired,
  materialItemFromCatalogProduct,
  normalizeQuoteCatalogProduct,
  quoteCatalogProductLabel,
} from '../quoteCatalogModel';

const money = (value, currency = 'EUR') => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency,
}).format(Number(value || 0));

export default function QuoteMaterialCatalogPicker({
  disabled,
  operational = false,
  partnerDiscountRate = 0,
  relativeQuantity = false,
  buttonLabel = 'Utiliser dans la ligne',
  onSelect,
}) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [explicitCost, setExplicitCost] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!search.trim()) {
      setProducts([]);
      setSelectedId('');
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError('');
        const response = await searchQuoteCatalogProducts({ search: search.trim() });
        const rows = Array.isArray(response) ? response : response?.items || [];
        if (active) setProducts(rows.map(normalizeQuoteCatalogProduct));
      } catch (requestError) {
        if (active) {
          setProducts([]);
          setError(requestError.message || 'Catalogue indisponible.');
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [search]);

  const selected = useMemo(
    () => products.find((product) => String(product.id) === String(selectedId)) || null,
    [products, selectedId],
  );
  const requiresExplicitCost = operational || selected?.purchaseCostKnown === false;

  const add = async () => {
    if (!selected) return;
    if (relativeQuantity && !/^\d+(?:\.\d{1,4})?$/.test(quantity)) { setError('Quantité locale invalide (quatre décimales maximum).'); return; }
    setAdding(true); setError('');
    try {
    if (operational) {
      if (!/^\d+(?:\.\d{1,4})?$/.test(explicitCost)) { setError('Renseignez explicitement le coût unitaire connu, y compris s’il est nul.'); return; }
      if (await onSelect({ id: selected.id, unitPurchasePrice: explicitCost, quantity: relativeQuantity ? quantity : '1', unit: selected.unit }) === false) return;
      setSearch(''); setProducts([]); setSelectedId(''); setExplicitCost(''); return;
    }
    if (requiresExplicitCost) {
      if (!/^\d+(?:\.\d{1,4})?$/.test(explicitCost)) { setError('Renseignez le coût unitaire connu.'); return; }
      if (await onSelect(materialItemFromCatalogProduct({ ...selected, grossUnitPurchasePrice: explicitCost, netUnitPurchasePrice: explicitCost, discountRate: '0' }, { purchasePriceMode: 'manual' })) === false) return;
      setSearch(''); setProducts([]); setSelectedId(''); setExplicitCost(''); return;
    }
    const usePartnerDiscount = Number(partnerDiscountRate || 0) > 0
      && Number(selected.discountRate || 0) === 0;
    if (await onSelect(materialItemFromCatalogProduct(selected, usePartnerDiscount ? {
      discountRate: Number(partnerDiscountRate),
      discountMode: 'partner',
    } : {})) === false) return;
    setSearch('');
    setProducts([]);
    setSelectedId('');
    } catch (requestError) { setError(requestError.message || 'Ajout refusé. La sélection est conservée.'); }
    finally { setAdding(false); }
  };

  return (
    <section className="quote-catalog-picker">
      <header>
        <div><strong>Catalogue matériel</strong><small>{operational ? 'La référence est capturée. Le catalogue équipement ne fournit pas de coût certifié : renseignez le coût connu.' : 'La référence et le tarif sont capturés dans le devis : une mise à jour ultérieure du catalogue ne modifie pas cette version.'}</small></div>
      </header>
      <label>
        <span>Rechercher</span>
        <input disabled={disabled || adding} type="search" value={search} placeholder="Fabricant, référence, désignation…" onChange={(event) => setSearch(event.target.value)} />
      </label>
      {loading && <small>Recherche en cours…</small>}
      {error && <p className="quote-catalog-picker__error">{error}</p>}
      {products.length > 0 && <label><span>Référence</span><select disabled={disabled || adding} value={selectedId} onChange={(event) => { setSelectedId(event.target.value); setExplicitCost(''); }}><option value="">Sélectionner</option>{products.map((product) => <option key={product.id} value={product.id}>{quoteCatalogProductLabel(product)}</option>)}</select></label>}
      {selected && <article className="quote-catalog-picker__preview">
        <div><strong>{quoteCatalogProductLabel(selected)}</strong><small>{selected.productFamily || 'Famille non renseignée'}{selected.supplierName ? ` · ${selected.supplierName}` : ''}</small></div>
        {requiresExplicitCost ? <label>Coût unitaire d’achat connu<input required disabled={disabled || adding} inputMode="decimal" value={explicitCost} onChange={event => setExplicitCost(event.target.value.replace(',', '.'))} /></label> : <><div><span>Prix catalogue</span><strong>{money(selected.grossUnitPurchasePrice, selected.currencyCode)}</strong></div>
        <div><span>Remise</span><strong>{selected.discountRate || 0} %</strong></div></>}
        {isCatalogPriceExpired({ priceValidUntilSnapshot: selected.priceValidUntil }) && <span className="quote-catalog-picker__warning">Tarif arrivé à échéance</span>}
        {relativeQuantity && <><label>Quantité locale du produit<input required disabled={disabled || adding} inputMode="decimal" value={quantity} onChange={event => setQuantity(event.target.value.replace(',', '.'))} /></label><small>Par unité du parent. Le moteur applique les quantités de tous les ancêtres.</small></>}
        <button className="basic-button active" disabled={disabled || adding} type="button" onClick={add}>{adding ? 'Ajout en cours…' : buttonLabel}</button>
      </article>}
    </section>
  );
}
